import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Button,
  Modal,
  ModalContent,
  ModalCloseButton,
  MobileSearchField,
  PlayerPickerSheet,
  StatusBadge,
} from '../common';
import { filterPlayersForHostSearch, getPlayerRosterTypeMeta } from '../../utils/clubPlayerLinkDedup';
import { playerMatchesSearchQuery } from '../../utils/playerUtils';
import { fetchClubPlayersSessionSummary } from '../../services/playerService';
import { isMobileSearchSheet, preventInputBlurOnPointerDown } from '../../utils/mobileSearchInput';
import useKeyboardAwareScrollMaxHeight from '../../hooks/useKeyboardAwareScrollMaxHeight';
import { AddNewGuestRow, parseSearchToGuestName } from '../common/AddNewGuestSearchRow';
import PasteRosterModal from '../sessions/PasteRosterModal';
import { membershipFromLiveStatus } from '../../utils/rosterPasteMatch';

function playerDisplayName(player) {
  if (!player) return '';
  return (
    player.name ||
    `${player.first_name || ''} ${player.last_name || ''}`.trim() ||
    'Unknown'
  );
}

function formatLastSession(dateString) {
  if (!dateString) return 'Never';
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return 'Never';
  }
}

function PlayerPickInfoButton({ player, sessionSummary, summaryLoading }) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const triggerRef = useRef(null);
  const typeMeta = getPlayerRosterTypeMeta(player);
  const summary = sessionSummary?.[player.id];

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const panelWidth = 240;
    const panelHeight = 120;
    let top = rect.bottom + 8;
    let left = rect.left + rect.width / 2 - panelWidth / 2;

    if (left + panelWidth > window.innerWidth - 8) {
      left = window.innerWidth - panelWidth - 8;
    }
    if (left < 8) left = 8;
    if (top + panelHeight > window.innerHeight - 8) {
      top = rect.top - panelHeight - 8;
    }
    if (top < 8) top = 8;

    setStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      width: `${panelWidth}px`,
      zIndex: 99999,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          // Row is a clickable "add player" surface — stop the info tap from also adding.
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-text-muted hover:bg-surface-subtle hover:text-text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 touch-manipulation"
        aria-label={`Player details for ${playerDisplayName(player)}`}
        aria-expanded={open}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            role="tooltip"
            className="rounded-lg border border-border-medium bg-surface-elevated px-3 py-2.5 text-left shadow-brand-md"
            style={style}
          >
            <p className="mb-2 text-xs font-semibold text-text-primary truncate">
              {playerDisplayName(player)}
            </p>
            <div className="mb-2">
              <StatusBadge variant={typeMeta.variant} size="small">
                {typeMeta.label}
              </StatusBadge>
            </div>
            {player.linkPending && (
              <p className="mb-2 text-xs text-text-muted">
                This player has been invited to link their registered account. They&apos;ll appear as a
                member once they accept.
              </p>
            )}
            {summaryLoading && !summary ? (
              <p className="text-xs text-text-muted">Loading session history…</p>
            ) : (
              <dl className="space-y-1 text-xs">
                <div className="flex justify-between gap-3">
                  <dt className="text-text-muted">Sessions</dt>
                  <dd className="font-medium tabular-nums text-text-primary">
                    {summary?.sessionsPlayed ?? 0}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-text-muted">Last session</dt>
                  <dd className="font-medium text-text-primary text-right">
                    {formatLastSession(summary?.lastSessionDate)}
                  </dd>
                </div>
              </dl>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Full-row tap target: name + info icon + action label. A single row-wide tap target means
 * mobile users never have to scroll further to reach a separate trailing button that could be
 * cut off at the bottom of the list.
 */
function PlayerRow({ player, busy, actionLabel, sessionSummary, summaryLoading, onAdd }) {
  const name = playerDisplayName(player);

  const activate = () => {
    if (!busy) onAdd(player.id);
  };

  return (
    <div
      role="button"
      tabIndex={busy ? -1 : 0}
      aria-disabled={busy}
      onMouseDown={preventInputBlurOnPointerDown}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      }}
      className={`flex w-full items-center justify-between gap-2 rounded-lg p-3 min-h-[44px] touch-manipulation text-left transition-colors ${
        busy ? 'opacity-60' : 'cursor-pointer bg-surface hover:bg-surface-subtle'
      }`}
    >
      <span className="flex min-w-0 flex-1 items-center gap-1">
        <span
          className={`min-w-0 truncate text-sm font-medium ${
            player.gender === 'female' ? 'text-female' : 'text-text-primary'
          }`}
        >
          {name}
        </span>
        <PlayerPickInfoButton player={player} sessionSummary={sessionSummary} summaryLoading={summaryLoading} />
      </span>
      <span className="shrink-0 text-xs font-medium text-primary">{busy ? 'Adding…' : actionLabel}</span>
    </div>
  );
}

/**
 * Search club roster and add a player to the session, with a path to create a new guest
 * when they are not on the roster yet.
 * Mobile opens a `PlayerPickerSheet`; desktop keeps the centered modal.
 * @param {'signed_up'|'active'} [sessionEntryStatus='signed_up'] — `active` puts them on the playing waitlist.
 * @param {(prefill: { firstName: string, lastName: string, searchQuery: string }) => void} [onAddNewGuest]
 * @param {(writableRows: Array) => Promise<object>} [onPasteRoster] — bulk add from a pasted name list.
 */
export default function AddExistingClubPlayerModal({
  isOpen,
  onClose,
  players = [],
  sessionPlayerStatus = {},
  onAddToSession,
  onAddNewGuest,
  onPasteRoster,
  sessionEntryStatus = 'signed_up',
  clubId = null,
  seasonId = null,
  currentUser = null,
}) {
  const [search, setSearch] = useState('');
  const [addingId, setAddingId] = useState(null);
  const [showPasteRoster, setShowPasteRoster] = useState(false);
  const [sessionSummary, setSessionSummary] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [isMobileSheet] = useState(() => isMobileSearchSheet());
  const searchAnchorRef = useRef(null);
  const listMaxHeight = useKeyboardAwareScrollMaxHeight(searchAnchorRef, { enabled: isOpen && !isMobileSheet });

  const eligibleIds = useMemo(
    () =>
      (players || [])
        .filter((p) => p?.id && sessionPlayerStatus[p.id] === undefined)
        .map((p) => p.id),
    [players, sessionPlayerStatus],
  );

  const eligibleIdsKey = eligibleIds.join(',');

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setAddingId(null);
      setShowPasteRoster(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !clubId || !seasonId || eligibleIds.length === 0) {
      setSessionSummary({});
      setSummaryLoading(false);
      return undefined;
    }

    let cancelled = false;
    setSummaryLoading(true);
    fetchClubPlayersSessionSummary(eligibleIds, clubId, seasonId)
      .then((map) => {
        if (!cancelled) setSessionSummary(map);
      })
      .catch((e) => {
        console.error('[AddExistingClubPlayerModal] session summary fetch failed:', e);
        if (!cancelled) setSessionSummary({});
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, clubId, seasonId, eligibleIdsKey, eligibleIds]);

  const available = useMemo(() => {
    const notInSession = (players || []).filter(
      (p) => p?.id && sessionPlayerStatus[p.id] === undefined,
    );
    const deduped = filterPlayersForHostSearch(notInSession, { sessionPlayerStatus });
    deduped.sort((a, b) =>
      playerDisplayName(a).localeCompare(playerDisplayName(b), undefined, { sensitivity: 'base' }),
    );
    return deduped.filter((p) => playerMatchesSearchQuery(p, search));
  }, [players, sessionPlayerStatus, search]);

  if (!isOpen) return null;

  const handleAdd = async (playerId) => {
    setAddingId(playerId);
    try {
      await onAddToSession(playerId);
    } catch (e) {
      console.error('[AddExistingClubPlayerModal]', e);
    } finally {
      setAddingId(null);
    }
  };

  const actionLabel = sessionEntryStatus === 'active' ? 'Add as playing' : 'Add as signed up';
  const searchTrimmed = search.trim();
  const showNoMatchGuestRow = Boolean(onAddNewGuest) && searchTrimmed && available.length === 0;
  const showGuestButton = Boolean(onAddNewGuest);
  const showPasteButton = Boolean(onPasteRoster);
  const allInSession = eligibleIds.length === 0;

  const statusHint = showGuestButton
    ? 'Search for a club player, paste a list of names, or add someone new as a guest.'
    : showPasteButton
      ? 'Search for a club player or paste a list of names.'
      : 'Search for a club player.';

  // Pinned above the results — always visible without scrolling. Hidden while the contextual
  // no-match row is showing to avoid two competing "add guest" CTAs at once.
  const guestActionButton =
    showGuestButton && !showNoMatchGuestRow ? (
      <Button
        type="button"
        variant="outline"
        size="small"
        className="w-full min-h-[44px] touch-manipulation sm:min-h-0"
        onClick={() => {
          const { firstName, lastName } = parseSearchToGuestName(search);
          onAddNewGuest({ firstName, lastName, searchQuery: searchTrimmed });
        }}
      >
        + Add new guest player
      </Button>
    ) : null;

  const pasteListButton = showPasteButton ? (
    <Button
      type="button"
      variant="outline"
      size="small"
      className="w-full min-h-[44px] touch-manipulation sm:min-h-0"
      onClick={() => setShowPasteRoster(true)}
      disabled={Boolean(addingId)}
    >
      Paste a list of names
    </Button>
  ) : null;

  const extraActions =
    guestActionButton || pasteListButton ? (
      <div className="flex flex-col gap-2">
        {guestActionButton}
        {pasteListButton}
      </div>
    ) : null;

  const addPlayerHelpSuffix = showPasteButton && showGuestButton
    ? ', paste a list of names, or add someone new as a guest'
    : showPasteButton
      ? ', or paste a list of names'
      : showGuestButton
        ? ', or add someone new as a guest'
        : '';

  const listContent = (
    <div className="space-y-1">
      {available.map((player) => (
        <PlayerRow
          key={player.id}
          player={player}
          busy={addingId === player.id}
          actionLabel={actionLabel}
          sessionSummary={sessionSummary}
          summaryLoading={summaryLoading}
          onAdd={handleAdd}
        />
      ))}
      {showNoMatchGuestRow ? (
        <AddNewGuestRow search={search} onAddNewGuest={onAddNewGuest} className="mt-1" />
      ) : null}
      {!searchTrimmed && allInSession && showGuestButton ? (
        <p className="p-2 text-center text-sm text-text-muted">
          All club players are already in this session.
        </p>
      ) : null}
    </div>
  );

  if (isMobileSheet) {
    const subtitle =
      sessionEntryStatus === 'active'
        ? 'Tap a player to add them to the queue, paste a list, or add a new guest.'
        : 'Tap a player to sign them up, paste a list, or add a new guest.';

    return (
      <>
        {!showPasteRoster ? (
          <PlayerPickerSheet
            isOpen={isOpen}
            onClose={onClose}
            title="Add player"
            subtitle={subtitle}
            searchTerm={search}
            onSearchChange={(e) => setSearch(e.target.value)}
            searchPlaceholder="Search by name…"
            belowSearch={extraActions}
            hasResults
          >
            {listContent}
          </PlayerPickerSheet>
        ) : null}
        {onPasteRoster ? (
          <PasteRosterModal
            isOpen={showPasteRoster}
            onClose={() => setShowPasteRoster(false)}
            mode="live"
            players={players}
            getMembership={(player) => membershipFromLiveStatus(sessionPlayerStatus[player.id])}
            sessionPlayerStatus={sessionPlayerStatus}
            clubId={clubId}
            seasonId={seasonId}
            currentUser={currentUser}
            onAccept={onPasteRoster}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
    <Modal isOpen={isOpen && !showPasteRoster} onClose={onClose}>
      <ModalContent size="medium" className="flex min-h-0 flex-col overflow-hidden">
        <ModalCloseButton onClose={onClose} />
        <div className="pr-8">
          <h2 className="text-lg font-semibold text-text-primary">Add player</h2>
          <p className="mt-1 text-sm text-text-secondary">
            {sessionEntryStatus === 'active' ? (
              <>
                Search for an existing club player to add as{' '}
                <span className="font-medium">playing</span>
                {addPlayerHelpSuffix}.
              </>
            ) : (
              <>
                Search for an existing club player to add as{' '}
                <span className="font-medium">signed up</span>
                {addPlayerHelpSuffix}.
              </>
            )}
          </p>
        </div>

        <div ref={searchAnchorRef} className="mt-4 shrink-0">
          <MobileSearchField
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="form-input w-full rounded-md border border-border bg-surface px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {extraActions ? <div className="mt-2 shrink-0">{extraActions}</div> : null}

        <div
          className="mt-3 min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-md border border-border-medium bg-surface-subtle/50 p-2"
          style={listMaxHeight != null ? { maxHeight: listMaxHeight } : undefined}
        >
          {available.length === 0 && !showNoMatchGuestRow && !allInSession && !searchTrimmed ? (
            <p className="p-4 text-center text-sm text-text-muted">{statusHint}</p>
          ) : null}
          {available.length === 0 && !showNoMatchGuestRow && allInSession && !showGuestButton ? (
            <p className="p-4 text-center text-sm text-text-muted">
              All club players are already in this session.
            </p>
          ) : null}
          {(available.length > 0 || showNoMatchGuestRow || showGuestButton || showPasteButton || (allInSession && showGuestButton)) ? (
            listContent
          ) : null}
        </div>
      </ModalContent>
    </Modal>
    {onPasteRoster ? (
      <PasteRosterModal
        isOpen={showPasteRoster}
        onClose={() => setShowPasteRoster(false)}
        mode="live"
        players={players}
        getMembership={(player) => membershipFromLiveStatus(sessionPlayerStatus[player.id])}
        sessionPlayerStatus={sessionPlayerStatus}
        clubId={clubId}
        seasonId={seasonId}
        currentUser={currentUser}
        onAccept={onPasteRoster}
      />
    ) : null}
    </>
  );
}
